/**
 * Session-level facts the chat header shows (de-slop CHAT-7).
 *
 * "ICF analysis completed" used to be guessed from the words in the last
 * assistant reply (any answer mentioning "ICF" and "PCC" counted), and
 * "Transcript ready" was shown whenever the session had any message. Both now
 * come from what the server says about the session, plus analyses this screen
 * watched finish. Pure module so it can be unit-tested.
 */
import type { ChatSession } from '../../types';

/** `session_type` values the backend uses for analysis sessions. */
const ANALYSIS_SESSION_TYPES = new Set(['analysis', 'analyze', 'hybrid']);

type SessionMeta = Pick<ChatSession, 'session_type' | 'source_document_id'> | null | undefined;

export function isAnalysisSession(session: SessionMeta): boolean {
  if (!session) return false;
  if (session.session_type && ANALYSIS_SESSION_TYPES.has(session.session_type)) return true;
  // A linked source transcript is only set by the analysis pipeline.
  return session.source_document_id != null;
}

export function hasLinkedTranscript(session: SessionMeta): boolean {
  return Boolean(session?.source_document_id);
}

/**
 * Whether the session on screen has an analysis: the server marks it, or an
 * analysis task for it completed while this screen was open (the session list
 * may not have been refetched yet).
 */
export function isSessionAnalyzed(
  sessionId: number | null,
  session: SessionMeta,
  completedAnalysisSessionIds: ReadonlySet<number>,
): boolean {
  if (sessionId == null) return false;
  return isAnalysisSession(session) || completedAnalysisSessionIds.has(sessionId);
}
